import React, { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import styled from 'styled-components'
import { useTheme } from 'styled-components'
import LogoTuba from '@/assets/logoTuba'
import LanguageSwitcher from '@/components/LanguageSwitcher.jsx'
import { appVersion, appHash } from '@/config/app'

const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  min-width: 100%;
  background-color: ${({ theme }) => theme.colors.primaryBg};
`

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: ${({ theme }) => theme.breakpoints.xl};
  flex: 1;
  min-height: 0;
  height: 80vh;
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.primaryBg};
  overflow: visible;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-top: 56px;
    padding-bottom: ${({ theme }) => theme.spacing.xxl};
    text-align: center;
  }
`

const IllustrationSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
  background-color: ${({ theme }) => theme.colors.primaryBg};
  overflow: visible;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`

const LogoBlock = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50vh;
  flex-shrink: 0;
`

const LogoPositionWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const IllustrationImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`

const VersionHash = styled.div`
  flex-shrink: 0;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.text};
  & span {
    display: block;
    color: inherit;
  }
  .version {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text};
  }
  .hash {
    margin: ${({ theme }) => theme.spacing.xs} 0 0;
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

const FormSection = styled.div`
  flex: 0 0 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 1;
  }
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  img {
    width: 40px;
    height: 40px;
    margin-right: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`

const AuthHeader = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  gap: ${({ theme }) => theme.spacing.md};
  z-index: ${({ theme }) => theme.zIndex.docked};
`

const Copyright = styled.div`
  flex-shrink: 0;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  .copyright {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  .version {
    margin-top: ${({ theme }) => theme.spacing.xs};
    margin-bottom: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text};
  }
  .hash {
    margin-top: ${({ theme }) => theme.spacing.xs};
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.textTertiary};
  }
  .version-hash-mobile {
    display: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .version-hash-mobile {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: ${({ theme }) => theme.spacing.xs};
    }
  }
`

const AuthLayout = () => {
  const theme = useTheme()

  useEffect(() => {
    if (!theme?.colors?.primaryBg) return
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = theme.colors.primaryBg
    return () => {
      document.body.style.backgroundColor = prev
    }
  }, [theme?.colors?.primaryBg])

  return (
    <AuthContainer>
      <AuthHeader>
        <LanguageSwitcher anonymous />
      </AuthHeader>
      <ContentWrapper>
        <IllustrationSection>
          <LogoBlock>
            <IllustrationImg
              src="https://gw.alipayobjects.com/zos/rmsportal/TVYTbAXWheQpRcWDaDMu.svg"
              alt="Login illustration"
            />
            <LogoPositionWrap>
              <LogoTuba width="40vw" height="40vh" backgroundFill={theme?.colors?.primaryBg} />
            </LogoPositionWrap>
          </LogoBlock>
          <VersionHash>
            <span className="version">v{appVersion || '0.0.1'}</span>
            {appHash ? <span className="hash">Id: {appHash}</span> : null}
          </VersionHash>
        </IllustrationSection>
        <FormSection>
          <Logo>
            <LogoTuba width="5rem" height="5rem" />
            <LogoText>Tuba</LogoText>
          </Logo>
          <Outlet />
        </FormSection>
      </ContentWrapper>
      <Copyright>
        <span className="copyright">Copyright © Tuba 2020–{new Date().getFullYear()}</span>
        <div className="version-hash-mobile">
          <span className="version">v{appVersion}</span>
          {appHash ? <span className="hash">Id: {appHash}</span> : null}
        </div>
      </Copyright>
    </AuthContainer>
  )
}

export default AuthLayout
